import { useContext, useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Box, Divider, Typography, CircularProgress, useMediaQuery, Button, Fab, Snackbar } from '@mui/material'
import { Copy, CopyCheck, Eye } from 'lucide-react'

import StudioNavbar from '../../components/nav/StudioNavbar'
import TabSection from './tabs/TabSection'
import { useAuth } from '../../context/SecurityContextProvider'
import { VideoPagePreview } from '../../components/videopage/preview/VideopagePreview'
import { LinksProvider } from '../../context/LinkProvider'
import { MediaProvider } from '../../context/MediaProvider'
import { VibioThemeContext } from '../../context/VibioThemeProvider'
import { ensurePrimaryPage } from '../../services/api/PageDataService'


const styles = {
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
    },
    root: {
        display: 'flex',
        flexDirection: 'column',
        // Page-level clamp: the studio's flex columns are shrink-to-fit, so a tab with wide
        // intrinsic content (e.g. Analytics) could otherwise push the document wider than the
        // viewport and make mobile browsers zoom the whole page out. `clip` stops it at the root
        // without turning root into a scroll container (so vertical scroll + the sticky desktop
        // preview keep working — unlike `hidden`, which would force overflow-y to auto).
        overflowX: 'clip',
    },
    main: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        m: 0,
        p: 0,
        gap: 1,
        height: '92vh',
        fontFamily: 'Catamaran',
        backgroundColor: 'primary.bg',
    },
    leftPane: (isMobile) => ({
        m: 0,
        p: 0,
        flex: 3,
        // Let the column shrink within the main flex row so wide tab content reflows/truncates
        // to the available width instead of forcing the column (and the page) wider.
        minWidth: 0,
        color: 'text.second',
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMobile ? '' : 'center',
    }),
    snackbarContent: {
        backgroundColor: 'primary.bg',
        color: 'text.second',
        borderRadius: '32px',
        fontFamily: 'inherit',
        fontSize: '1rem',
        fontWeight: 500,
        boxShadow: '0px 3px 4px #000',
        m: '.4rem',
        p: '.6rem 1rem',
    },
    greetingText: {
        fontWeight: 400,
        fontFamily: 'inherit',
        textAlign: 'left',
        m: '.8rem',
    },
    mobileInstruction: {
        fontWeight: 500,
        fontFamily: 'inherit',
        textAlign: 'left',
    },
    copyButton: (isMobile) => ({
        fontFamily: 'inherit',
        fontWeight: 500,
        fontSize: '1rem',
        background: '#000',
        color: 'text.regular',
        boxShadow: '1px 2px 0px #ff1493',
        m: isMobile ? '.4rem .2rem' : '1rem 1rem 1rem 1.6rem',
        textTransform: 'none',
        borderRadius: '20px',
        '&:hover': { background: '#202020' },
    }),
    rightPane: {
        flex: 2,
        backgroundColor: 'primary.second',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'start',
        m: 0,
        p: '1.8rem 2rem',
    },
    previewWrapper: {
        position: 'sticky',
        top: '2.2rem',
    },
    fab: {
        position: 'fixed',
        bottom: '24px',
        right: '16px',
        zIndex: 1000,
        background: '#000',
        color: 'text.regular',
        fontWeight: 550,
    },
}

export default function StudioPage() {
    const { userData, loading, isAuthenticated } = useAuth()
    const navigate = useNavigate()
    const isMobile = useMediaQuery('(max-width:1050px)')
    const { updateTheme } = useContext(VibioThemeContext)
    const [page, setPage] = useState(null)
    const [state, setState] = useState({
        isOpen: false,
        vertical: 'top',
        horizontal: 'center',
    })

    useEffect(() => {
        if (userData !== undefined && userData !== null) {
            if (userData.didCompleteSetup === false) {
                navigate('/welcome')
            }
        }
    }, [userData, navigate])

    // New accounts have no page; ensurePrimaryPage creates one (+ default theme) on first load.
    useEffect(() => {
        if (userData?.didCompleteSetup) {
            ensurePrimaryPage()
                .then((primaryPage) => {
                    setPage(primaryPage)
                    if (primaryPage?.theme) {
                        updateTheme(primaryPage.theme) // seed the live-preview/styling theme
                    }
                })
                .catch((error) => console.error('Failed to load page', error))
        }
    }, [userData?.didCompleteSetup, updateTheme])

    if (loading) {
        return (
            <Box sx={styles.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" />
    }

    // Hold the studio until the primary page (links/socials/theme/videoSrc) is loaded.
    if (!page) {
        return (
            <Box sx={styles.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    const videoSrc = page.videoSrc || ""

    const handleOpenSnackbar = () => {
        setState({ ...state, isOpen: true })
        setTimeout(() => {
            setState({ ...state, isOpen: false })
        }, 2000)
    }

    return (
        <LinksProvider user={userData} page={page}>
            <MediaProvider user={userData} page={page}>
                <Box sx={styles.root}>
                    <StudioNavbar />
                    <Box sx={styles.main}>
                        {/* LEFT PANE */}
                        <Box sx={styles.leftPane(isMobile)}>
                            <Snackbar
                                anchorOrigin={{ vertical: state.vertical, horizontal: state.horizontal }}
                                open={state.isOpen}
                                message={
                                    <span style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
                                        Copied! <CopyCheck />
                                    </span>
                                }
                                key={`${state.vertical}-${state.horizontal}`}
                                ContentProps={{ sx: styles.snackbarContent }}
                            />
                            <Box>
                                <section style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        justifyContent: isMobile ? 'space-evenly' : 'space-between',
                                        margin: isMobile ? '0 .2rem' : '.4rem .2rem',
                                    }}>
                                        {!isMobile && (
                                            <Typography variant="h6" component="h5" sx={styles.greetingText}>
                                                Hello,
                                                <span style={{ fontWeight: 500 }}>
                                                    {" " + userData?.username}
                                                </span>
                                                !
                                            </Typography>
                                        )}
                                        {isMobile && (
                                            <Typography variant="body1" component="p" sx={styles.mobileInstruction}>
                                                Copy & share your link
                                            </Typography>
                                        )}
                                        <Button
                                            variant="filled"
                                            color="primary"
                                            sx={styles.copyButton(isMobile)}
                                            endIcon={<Copy />}
                                            onClick={async () => {
                                                await navigator.clipboard.writeText(`https://vibio.co/${userData?.username}`)
                                                handleOpenSnackbar()
                                            }}
                                        >
                                            {`vibio.co/${userData?.username}`}
                                        </Button>
                                    </div>
                                </section>
                                <Divider />
                                <TabSection user={userData} page={page} />
                            </Box>
                        </Box>

                        {/* RIGHT PANE */}
                        {!isMobile && (
                            <Box sx={styles.rightPane}>
                                <div style={styles.previewWrapper}>
                                    <VideoPagePreview userData={userData} videoSrc={videoSrc} />
                                </div>
                            </Box>
                        )}
                    </Box>

                    {/* FAB for mobile */}
                    {isMobile && (
                        <Fab
                            variant="extended"
                            onClick={() => navigate(`/${userData?.username}?preview=true`)}
                            sx={styles.fab}
                        >
                            <Eye style={{ marginRight: '.5rem' }} />
                            Preview
                        </Fab>
                    )}
                </Box>
            </MediaProvider>
        </LinksProvider>
    )
}
